import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck, Search, RotateCcw, Download, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';

const METHOD_COLORS = {
  POST: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  PUT: 'bg-amber-100 text-amber-700 border-amber-200',
  PATCH: 'bg-amber-100 text-amber-700 border-amber-200',
  DELETE: 'bg-rose-100 text-rose-700 border-rose-200',
};

const formatTs = (ts) => {
  if (!ts) return '-';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return ts;
  }
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total_count: 0, total_pages: 0 });
  const [filterOptions, setFilterOptions] = useState({ resources: [], methods: [], actor_types: [] });

  const [filters, setFilters] = useState({
    search: '', ip: '', method: ALL, resource: ALL, actor_type: ALL,
    start_date: '', end_date: '',
  });
  const [page, setPage] = useState(1);

  const buildParams = useCallback((pageNum) => {
    const p = { page: pageNum, page_size: 50, plant_id: 'all' };
    if (filters.search) p.search = filters.search;
    if (filters.ip) p.ip = filters.ip;
    if (filters.method !== ALL) p.method = filters.method;
    if (filters.resource !== ALL) p.resource = filters.resource;
    if (filters.actor_type !== ALL) p.actor_type = filters.actor_type;
    if (filters.start_date) p.start_date = filters.start_date;
    if (filters.end_date) p.end_date = filters.end_date;
    return p;
  }, [filters]);

  const fetchLogs = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-logs`, { params: buildParams(pageNum) });
      setLogs(res.data.data || []);
      setPagination(res.data.pagination || {});
      setPage(pageNum);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    axios.get(`${API}/audit-logs/filters`, { params: { plant_id: 'all' } })
      .then((r) => setFilterOptions(r.data || { resources: [], methods: [], actor_types: [] }))
      .catch(() => {});
    fetchLogs(1);
  }, []);

  const resetFilters = () => {
    setFilters({ search: '', ip: '', method: ALL, resource: ALL, actor_type: ALL, start_date: '', end_date: '' });
    setTimeout(() => fetchLogs(1), 0);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/audit-logs`, { params: { ...buildParams(1), page_size: 200 } });
      const rows = res.data.data || [];
      if (rows.length === 0) { toast.info('No records to export'); return; }
      const headers = ['Timestamp', 'User', 'Type', 'Role', 'IP', 'Browser', 'Method', 'Action', 'Status'];
      const csv = [headers.join(',')].concat(
        rows.map((l) => [
          formatTs(l.timestamp), l.actor_name, l.actor_type, l.actor_role || '',
          l.ip, l.browser, l.method, l.action, l.status_code,
        ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      ).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-indigo-600" />
          <div>
            <h2 className="text-lg font-bold text-slate-800">Audit Log</h2>
            <p className="text-sm text-slate-500">Track who did what, when, and from which IP address.</p>
          </div>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="audit-export-btn">
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <Input
              placeholder="Search user / action / IP"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1)}
              data-testid="audit-search-input"
            />
            <Input
              placeholder="IP address"
              value={filters.ip}
              onChange={(e) => setFilters({ ...filters, ip: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && fetchLogs(1)}
              data-testid="audit-ip-input"
            />
            <Select value={filters.actor_type} onValueChange={(v) => setFilters({ ...filters, actor_type: v })}>
              <SelectTrigger data-testid="audit-actor-type-select"><SelectValue placeholder="User type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All user types</SelectItem>
                {filterOptions.actor_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.method} onValueChange={(v) => setFilters({ ...filters, method: v })}>
              <SelectTrigger data-testid="audit-method-select"><SelectValue placeholder="Method" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All methods</SelectItem>
                {filterOptions.methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.resource} onValueChange={(v) => setFilters({ ...filters, resource: v })}>
              <SelectTrigger data-testid="audit-resource-select"><SelectValue placeholder="Module" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All modules</SelectItem>
                {filterOptions.resources.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex flex-col">
              <label className="text-xs text-slate-500 mb-1">From</label>
              <Input type="date" value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                data-testid="audit-start-date" />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-slate-500 mb-1">To</label>
              <Input type="date" value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                data-testid="audit-end-date" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={() => fetchLogs(1)} className="flex-1" data-testid="audit-apply-btn">
                <Search className="h-4 w-4 mr-2" /> Apply
              </Button>
              <Button variant="outline" onClick={resetFilters} data-testid="audit-reset-btn">
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <span className="text-sm text-slate-600" data-testid="audit-total-count">
              Total: <strong>{pagination.total_count || 0}</strong> entries
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1 || loading}
                onClick={() => fetchLogs(page - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">Page {page} / {pagination.total_pages || 1}</span>
              <Button variant="outline" size="sm" disabled={page >= (pagination.total_pages || 1) || loading}
                onClick={() => fetchLogs(page + 1)} data-testid="audit-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No audit entries found</TableCell></TableRow>
                ) : logs.map((l) => (
                  <TableRow key={l.id} data-testid="audit-row">
                    <TableCell className="whitespace-nowrap text-sm">{formatTs(l.timestamp)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium text-slate-800 text-sm">{l.actor_name}</span>
                        <span className="text-xs text-slate-400">{l.actor_type}{l.actor_role ? ` · ${l.actor_role}` : ''}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-sm font-mono text-slate-600">
                        <Globe className="h-3 w-3 text-slate-400" />{l.ip}
                      </span>
                      <div className="text-xs text-slate-400">{l.browser}</div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${METHOD_COLORS[l.method] || ''}`}>{l.method}</Badge>
                        <span className="text-sm text-slate-700 truncate" title={l.action}>{l.action}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm capitalize text-slate-600">{l.resource || '-'}</TableCell>
                    <TableCell>
                      {l.success ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">{l.status_code || 'OK'}</Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">{l.status_code || 'FAIL'}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
